// @ts-nocheck

import type {
    FilterSpecification,
    LayerSpecification,
    SymbolLayerSpecification,
} from 'maplibre-gl'

import {
    AUDIENCE_MAP,
    CATEGORY_MAP,
    CATEGORY_MAP_DOT,
    DECOR_MAP,
    EVENT_MAP,
} from './sources'

const isBrowser = typeof window !== 'undefined'
const clientWidth = isBrowser ? window.innerWidth : 1024

const DEFAULT_FILTERS: FilterSpecification[] = [
    ['==', 'is_hidden', false],
    ['==', 'is_perma_close', false],
]

export function createMapFilter(
    ...filters: FilterSpecification[]
): FilterSpecification {
    return ['all', ...DEFAULT_FILTERS, ...filters] as FilterSpecification
}

export { DEFAULT_FILTERS as DEFAULT_MAP_FILTERS }

type SymbolLayout = NonNullable<SymbolLayerSpecification['layout']>
type SymbolPaint = NonNullable<SymbolLayerSpecification['paint']>

type Props = {
    text_color: string
    halo_color: string
}

type LayerConfig = {
    id: string
    filter?: FilterSpecification
    layout: SymbolLayout
    paint?: SymbolPaint
}

const BASE_LAYOUT: SymbolLayout = {
    'text-font': ['Vazir Regular'],
    'text-field': '{name}',
    'text-allow-overlap': false,
    'icon-ignore-placement': false,
    'icon-anchor': 'bottom',
    'text-anchor': 'top',
    'icon-offset': [0, 0],
    'text-size': {
        stops: [
            [1, 0],
            [clientWidth > 768 ? 12 : 13, 0],
            [clientWidth > 768 ? 12.5 : 13.5, 16],
        ],
    },
    visibility: 'visible',
}

const CATEGORY_DOT_LAYOUT: SymbolLayout = {
    'icon-image': CATEGORY_MAP_DOT,
    'icon-allow-overlap': false,
    'icon-ignore-placement': false,
    visibility: 'visible',
}

const CATEGORY_LAYOUT: SymbolLayout = {
    ...BASE_LAYOUT,
    'icon-image': CATEGORY_MAP,
    'icon-size': 0.75,
}

const DECOR_LAYOUT: SymbolLayout = {
    ...BASE_LAYOUT,
    'icon-image': DECOR_MAP,
    'icon-size': 0.5,
    visibility: 'none',
}

const EVENT_LAYOUT: SymbolLayout = {
    ...BASE_LAYOUT,
    'icon-image': EVENT_MAP,
    'icon-size': 1,
    visibility: 'none',
}

const AUDIENCE_LAYOUT: SymbolLayout = {
    ...BASE_LAYOUT,
    'icon-image': AUDIENCE_MAP,
    'icon-size': 0.5,
    visibility: 'none',
}

const createBaseFilter = (
    ...filters: FilterSpecification[]
): FilterSpecification =>
    ['all', ...DEFAULT_FILTERS, ...filters] as FilterSpecification

const createPaint = (
    text_color: string,
    halo_color: string,
    options: {
        iconOpacity?: number
        textOpacity?: number
    } = {}
): SymbolPaint => ({
    'text-color': text_color,
    'text-halo-color': halo_color,
    'text-halo-width': 1.1,
    'text-halo-blur': 0,
    'text-translate': [0, 0],
    'icon-translate': [0, -3],

    ...(options.iconOpacity !== undefined
        ? { 'icon-opacity': options.iconOpacity }
        : {}),

    ...(options.textOpacity !== undefined
        ? { 'text-opacity': options.textOpacity }
        : {}),
})

const createLayer = (config: LayerConfig): LayerSpecification => ({
    id: config.id,
    type: 'symbol',
    source: 'gooje',
    'source-layer': 'eatery',
    filter: config.filter ?? createBaseFilter(),
    layout: config.layout,
    ...(config.paint ? { paint: config.paint } : {}),
})

export function eatery_layers(P: Props): LayerSpecification[] {
    const { text_color, halo_color } = P

    const normalPaint = createPaint(text_color, halo_color)

    const lowQualityPaint = createPaint(text_color, halo_color, {
        iconOpacity: 0.7,
        textOpacity: 0.7,
    })

    const normalLayers: LayerConfig[] = [
        {
            id: 'eatery low-quality',
            filter: createBaseFilter(['==', 'is_low_quality', true]),
            layout: CATEGORY_LAYOUT,
            paint: lowQualityPaint,
        },

        {
            id: 'eatery dot normal',
            filter: createBaseFilter(['==', 'is_low_quality', false]),
            layout: CATEGORY_DOT_LAYOUT,
        },

        {
            id: 'eatery normal',
            filter: createBaseFilter(['==', 'is_low_quality', false]),
            layout: CATEGORY_LAYOUT,
            paint: normalPaint,
        },

        {
            id: 'eatery with-photos',
            filter: createBaseFilter(
                ['==', 'is_low_quality', false],
                ['>', 'photos', 0]
            ),
            layout: CATEGORY_LAYOUT,
            paint: normalPaint,
        },

        {
            id: 'eatery with-menu',
            filter: createBaseFilter(
                ['==', 'is_low_quality', false],
                ['==', 'has_menu', true]
            ),
            layout: CATEGORY_LAYOUT,
            paint: normalPaint,
        },
    ]

    const filterLayers: LayerConfig[] = [
        {
            id: 'eatery filter-event',
            layout: EVENT_LAYOUT,
            paint: normalPaint,
        },

        {
            id: 'eatery filter-decor',
            layout: DECOR_LAYOUT,
            paint: normalPaint,
        },

        {
            id: 'eatery filter-audience',
            layout: AUDIENCE_LAYOUT,
            paint: normalPaint,
        },

        {
            id: 'eatery filter-group',
            layout: {
                ...CATEGORY_LAYOUT,
                visibility: 'none',
            },
            paint: normalPaint,
        },

        {
            id: 'eatery filter-domain',
            layout: {
                ...CATEGORY_LAYOUT,
                visibility: 'none',
            },
            paint: normalPaint,
        },
    ]

    return [...normalLayers.map(createLayer), ...filterLayers.map(createLayer)]
}
